<script setup lang="tsx">
import { ref, computed, reactive } from 'vue'
import { useI18n } from 'vue-i18n'
import Modal from '@/components/shared/Modal.vue'
import IconButton from '@/components/shared/IconButton.vue'
import ImportFileUpload from './import/ImportFileUpload.vue'
import ImportMatchedCard from './import/ImportMatchedCard.vue'
import ImportUnmatchedCard from './import/ImportUnmatchedCard.vue'
import ImportSummaryPanel from './import/ImportSummaryPanel.vue'
import ImportConfirmPanel from './import/ImportConfirmPanel.vue'
import http from '@/services/http'
import methodEditor from '@/stores/method-editor'
import { hasPermission, viewing } from '@/services/utils'
import { PermAdminMeasureImport, NotImplemented } from '@/models/enums-generated'
import type {
	Response,
	MethodImportMatchedMethod as MatchedMethod,
	MethodImportUnmatchedMethod as UnmatchedMethod,
	MethodImportNameMismatch as NameMismatch,
	MethodImportColumnInfo as ColumnInfo,
	MethodImportPreviewResult as PreviewResult,
	MethodImportSuggestedItem as SuggestedItem,
} from '@/models/api-generated'
import {
	hasDiff,
	hasStatusMismatch,
	effectiveStatus as _effectiveStatus,
	isDoNotImport as _isDoNotImport,
	DoNotImport,
} from './import/importUtils'

const { t } = useI18n()
const editor = methodEditor()

const isVisible = ref(false)
const step = ref<1 | 2 | 3>(1)
const isLoading = ref(false)

const selectedFile = ref<File | undefined>()
const preview = ref<PreviewResult | null>(null)

const moduleAssets = ref<Record<string, SuggestedItem>>({})
const moduleBps = ref<Record<string, SuggestedItem[]>>({})
const moduleBpInputs = ref<Record<string, SuggestedItem | null>>({})

const selection = reactive({
	matched: new Set<number>(),
	unmatched: new Set<number>(),
})

const expanded = reactive({
	matched: new Set<string>(),
	unmatched: new Set<string>(),
	confirmMatched: new Set<string>(),
	confirmPlanned: new Set<string>(),
	moduleIds: new Set<number>(),
})

const showMappingStep = ref(false)
const columnInfo = ref<ColumnInfo>({ headers: [], detected: {} })
const userColumnMapping = ref<Record<string, number | null>>({})

const approvedVersionMismatch = ref(false)
const highSecDeselected = ref(false)
const statusReplacementMap = ref<Record<string, string>>({})

const canShow = computed(() => viewing.value > 0 && hasPermission(PermAdminMeasureImport))

const canonicalFieldDefs = computed(() => [
	{ key: 'name', label: t('method.reference'), required: true },
	{ key: 'assetName', label: t('asset.label', 1), required: false },
	{ key: 'businessProcesses', label: t('businessProcess.label', 2), required: false },
	{ key: 'status', label: t('method.status', 1), required: false },
	{ key: 'responsible', label: t('import.responsible'), required: false },
	{ key: 'dueDate', label: t('date.dueDate', 1), required: false },
	{ key: 'comment', label: t('misc.comment'), required: false },
	{ key: 'description', label: t('import.methodDescription'), required: false },
])

const unmatchedModules = computed(() => {
	if (!preview.value) return []
	const seen = new Set<string>()
	const mods: { groupKey: string; moduleId: number; moduleName: string; moduleType: string }[] = []
	for (const m of preview.value.unmatched) {
		const key = unmatchedGroupKey(m)
		if (!seen.has(key)) {
			seen.add(key)
			mods.push({ groupKey: key, moduleId: m.moduleId, moduleName: m.module, moduleType: m.moduleType })
		}
	}
	return mods
})

const totalFoundCount = computed(() => (preview.value?.matched.length ?? 0) + (preview.value?.unmatched.length ?? 0))
const matchedChangesCount = computed(() => (preview.value?.matched ?? []).filter(hasDiff).length)
const matchedNoChangesCount = computed(() => (preview.value?.matched ?? []).filter((m) => !hasDiff(m)).length)
const noAssetCount = computed(() => {
	if (!preview.value) return 0
	return preview.value.unmatched.filter((m) => {
		if (!selection.unmatched.has(m.methodId)) return false
		if (m.moduleType === 'P') return false
		const key = unmatchedGroupKey(m)
		return !moduleAssets.value[key]?.id || isExcludedAssetSelected(key)
	}).length
})
const noBpCount = computed(() => {
	if (!preview.value) return 0
	return preview.value.unmatched.filter((m) => {
		if (!selection.unmatched.has(m.methodId)) return false
		if (m.moduleType === 'P') return false
		return (moduleBps.value[unmatchedGroupKey(m)] ?? []).length === 0
	}).length
})
const highSecNeedCount = computed(
	() =>
		(preview.value?.matched ?? []).filter((m) => m.highSecNeedWarning).length +
		(preview.value?.unmatched ?? []).filter((m) => m.highSecNeedWarning).length,
)
const statusMismatchCount = computed(() => Object.values(invalidStatusCounts.value).reduce((sum, n) => sum + n, 0))
const responsibleNotFoundCount = computed(
	() =>
		[...(preview.value?.matched ?? []), ...(preview.value?.unmatched ?? [])].filter(
			(m) => m.imported.responsibleLabel && !m.imported.responsibleId,
		).length,
)
const missingResponsibleLabels = computed<string[]>(() => {
	if (!preview.value) return []
	const labels = new Set<string>()
	for (const m of [...preview.value.matched, ...preview.value.unmatched]) {
		if (m.imported.responsibleLabel && !m.imported.responsibleId) labels.add(m.imported.responsibleLabel)
	}
	return [...labels]
})
const selectedUnmatchedCount = computed(() => (preview.value?.unmatched ?? []).filter((m) => selection.unmatched.has(m.methodId)).length)
const selectedCount = computed(() => selection.matched.size + selectedUnmatchedCount.value)
const readyToImportCount = computed(() => {
	const statusOk = (status: string) => !hasStatusMismatch(status) || !!statusReplacementMap.value[status]
	const matchedReady = (preview.value?.matched ?? []).filter((m) => selection.matched.has(m.metaId) && statusOk(m.imported.status)).length
	return matchedReady + plannedActivations.value.filter((m) => statusOk(m.imported.status)).length
})
const missingRequirementsCount = computed(() => {
	const matchedMissing = (preview.value?.matched ?? []).filter(
		(m) => selection.matched.has(m.metaId) && hasStatusMismatch(m.imported.status) && !statusReplacementMap.value[m.imported.status],
	).length
	const plannedMissing = plannedActivations.value.filter(
		(m) => hasStatusMismatch(m.imported.status) && !statusReplacementMap.value[m.imported.status],
	).length
	return matchedMissing + skippedUnmatched.value.length + plannedMissing
})

const moduleExcludedAssetIds = computed(() => {
	const result: Record<string, Set<number>> = {}
	for (const m of preview.value?.unmatched ?? []) {
		const key = unmatchedGroupKey(m)
		if (!result[key]) result[key] = new Set()
		for (const id of m.existingAssetIds ?? []) result[key].add(id)
	}
	return result
})

const moduleSiblingSelectedAssetIds = computed(() => {
	const result: Record<string, Set<number>> = {}
	const serviceEntries = Object.entries(unmatchedByGroup.value).filter(([, methods]) => methods.length && methods[0].moduleType === 'S')
	const keysByModule = serviceEntries.reduce<Record<number, string[]>>((acc, [key, methods]) => {
		;(acc[methods[0].moduleId] ??= []).push(key)
		return acc
	}, {})
	for (const keys of Object.values(keysByModule)) {
		if (keys.length < 2) continue
		for (const key of keys) {
			result[key] = new Set(
				keys
					.filter((k) => k !== key)
					.map((k) => moduleAssets.value[k]?.id)
					.filter((id): id is number => !!id),
			)
		}
	}
	return result
})

const unmatchedByGroup = computed(() => {
	if (!preview.value) return {}
	const map: Record<string, UnmatchedMethod[]> = {}
	for (const m of preview.value.unmatched) {
		const key = unmatchedGroupKey(m)
		if (!map[key]) map[key] = []
		map[key].push(m)
	}
	return map
})

const unmatchedByGroupImportable = computed(() => {
	const result: Record<string, UnmatchedMethod[]> = {}
	for (const [key, methods] of Object.entries(unmatchedByGroup.value)) {
		result[key] = methods.filter((m) => !isDoNotImport(m.imported.status))
	}
	return result
})

const moduleRestoreInfo = computed(() => {
	const info: Record<string, { isRestore: boolean; restoreAssetName: string; totalModuleMethods: number }> = {}
	for (const [key, methods] of Object.entries(unmatchedByGroup.value)) {
		const restoreMethod = methods.find((m) => m.restoreConnection)
		info[key] = {
			isRestore: !!restoreMethod,
			restoreAssetName: restoreMethod?.restoreAssetName ?? '',
			totalModuleMethods: methods[0]?.totalModuleMethods ?? 0,
		}
	}
	return info
})

const hasAnythingToImport = computed(() => !!preview.value && (preview.value.matched.length > 0 || preview.value.unmatched.length > 0))

const mismatchByName = computed(() => {
	const map: Record<string, NameMismatch> = {}
	for (const mm of preview.value?.nameMismatches ?? []) map[mm.name] = mm
	return map
})

const hasAnyVersionMismatch = computed(
	() =>
		(preview.value?.unmatched ?? []).some((m) => !!mismatchByName.value[m.name]) ||
		(preview.value?.matched ?? []).some((m) => !!mismatchByName.value[m.name]),
)

const hasUnresolvedStatusMismatch = computed(() => {
	const sel = [
		...(preview.value?.matched ?? []).filter((m) => selection.matched.has(m.metaId)),
		...(preview.value?.unmatched ?? []).filter((m) => selection.unmatched.has(m.methodId)),
	]
	return sel.some((m) => hasStatusMismatch(m.imported.status) && !statusReplacementMap.value[m.imported.status])
})

const uniqueInvalidStatuses = computed(() => {
	const seen = new Set<string>()
	const result: string[] = []
	for (const s of [
		...(preview.value?.unmatched ?? []).map((m) => m.imported.status),
		...(preview.value?.matched ?? []).map((m) => m.imported.status),
	]) {
		if (hasStatusMismatch(s) && !seen.has(s)) {
			seen.add(s)
			result.push(s)
		}
	}
	return result
})

const invalidStatusCounts = computed(() => {
	const counts: Record<string, number> = {}
	for (const m of [...(preview.value?.unmatched ?? []), ...(preview.value?.matched ?? [])]) {
		if (hasStatusMismatch(m.imported.status)) {
			counts[m.imported.status] = (counts[m.imported.status] ?? 0) + 1
		}
	}
	return counts
})

const plannedActivations = computed(() => {
	if (!preview.value) return []
	return preview.value.unmatched.filter((m) => {
		if (!selection.unmatched.has(m.methodId)) return false
		if (m.moduleType === 'P') return true
		const key = unmatchedGroupKey(m)
		return !!moduleAssets.value[key]?.id && !isExcludedAssetSelected(key) && (moduleBps.value[key] ?? []).length > 0
	})
})

const skippedUnmatched = computed(() => {
	if (!preview.value) return []
	return preview.value.unmatched.filter((m) => {
		if (!selection.unmatched.has(m.methodId)) return false
		if (m.moduleType === 'P') return false
		const key = unmatchedGroupKey(m)
		return !moduleAssets.value[key]?.id || isExcludedAssetSelected(key) || (moduleBps.value[key] ?? []).length === 0
	})
})

const uniquePlannedModules = computed(() => {
	const seen = new Set<string>()
	return plannedActivations.value
		.filter((m) => {
			const key = unmatchedGroupKey(m)
			if (seen.has(key)) return false
			seen.add(key)
			return true
		})
		.map((m) => ({ ...m, groupKey: unmatchedGroupKey(m) }))
})

const plannedByGroup = computed(() => {
	const map: Record<string, UnmatchedMethod[]> = {}
	for (const m of plannedActivations.value) {
		const key = unmatchedGroupKey(m)
		if (!map[key]) map[key] = []
		map[key].push(m)
	}
	return map
})

const matchedGroupKeys = computed(() => {
	if (!preview.value) return []
	const seen = new Set<string>()
	return preview.value.matched
		.filter((m) => {
			const key = `${m.module}__${m.assetName}`
			if (seen.has(key)) return false
			seen.add(key)
			return true
		})
		.map((m) => ({ key: `${m.module}__${m.assetName}`, module: m.module, moduleTitle: m.moduleTitle, assetName: m.assetName }))
})

const matchedByGroup = computed(() => {
	if (!preview.value) return {}
	const map: Record<string, MatchedMethod[]> = {}
	for (const m of preview.value.matched) {
		const key = `${m.module}__${m.assetName}`
		if (!map[key]) map[key] = []
		map[key].push(m)
	}
	return map
})

const matchedByGroupImportable = computed(() => {
	const result: Record<string, MatchedMethod[]> = {}
	for (const [key, methods] of Object.entries(matchedByGroup.value)) {
		result[key] = methods.filter((m) => hasDiff(m) && !isDoNotImport(m.imported.status))
	}
	return result
})

const confirmedMatchedGroups = computed(() =>
	matchedGroupKeys.value.filter((grp) => (matchedByGroup.value[grp.key] ?? []).some((m) => selection.matched.has(m.metaId))),
)

const confirmedMatchedByGroup = computed(() => {
	const map: Record<string, MatchedMethod[]> = {}
	for (const [key, methods] of Object.entries(matchedByGroup.value)) {
		const selected = methods.filter((m) => selection.matched.has(m.metaId))
		if (selected.length > 0) map[key] = selected
	}
	return map
})

function toggle<T>(set: Set<T>, key: T) {
	if (set.has(key)) set.delete(key)
	else set.add(key)
}

function unmatchedGroupKey(m: UnmatchedMethod): string {
	return m.moduleType === 'S' ? `${m.moduleId}:${m.importedAssetName}` : String(m.moduleId)
}

function isDoNotImport(status: string): boolean {
	return _isDoNotImport(status, statusReplacementMap.value)
}

function isSiblingDuplicateAsset(groupKey: string): boolean {
	const selectedId = moduleAssets.value[groupKey]?.id
	if (!selectedId) return false
	return moduleSiblingSelectedAssetIds.value[groupKey]?.has(selectedId) ?? false
}

function isExcludedAssetSelected(groupKey: string): boolean {
	const selectedId = moduleAssets.value[groupKey]?.id
	if (!selectedId) return false
	return (moduleExcludedAssetIds.value[groupKey]?.has(selectedId) ?? false) || isSiblingDuplicateAsset(groupKey)
}

function setStatusReplacement(status: string, value: string) {
	statusReplacementMap.value = { ...statusReplacementMap.value, [status]: value }
	if (value === DoNotImport) {
		for (const m of preview.value?.matched ?? []) {
			if (m.imported.status === status) selection.matched.delete(m.metaId)
		}
		for (const m of preview.value?.unmatched ?? []) {
			if (m.imported.status === status) selection.unmatched.delete(m.methodId)
		}
	}
}

function toggleHighSecDeselect() {
	const adding = highSecDeselected.value
	for (const m of preview.value?.unmatched ?? []) {
		if (m.highSecNeedWarning) {
			if (adding) selection.unmatched.add(m.methodId)
			else selection.unmatched.delete(m.methodId)
		}
	}
	for (const m of preview.value?.matched ?? []) {
		if (m.highSecNeedWarning) {
			if (adding) selection.matched.add(m.metaId)
			else selection.matched.delete(m.metaId)
		}
	}
	highSecDeselected.value = !highSecDeselected.value
}

function open() {
	step.value = 1
	selectedFile.value = undefined
	preview.value = null
	moduleAssets.value = {}
	moduleBps.value = {}
	moduleBpInputs.value = {}
	selection.matched.clear()
	selection.unmatched.clear()
	expanded.matched.clear()
	expanded.unmatched.clear()
	expanded.confirmMatched.clear()
	expanded.confirmPlanned.clear()
	expanded.moduleIds.clear()
	approvedVersionMismatch.value = false
	highSecDeselected.value = false
	statusReplacementMap.value = {}
	showMappingStep.value = false
	isVisible.value = true
}

function close() {
	isVisible.value = false
}

function addBp(groupKey: string, bp: SuggestedItem | null) {
	if (!bp) return
	const current = moduleBps.value[groupKey] ?? []
	if (!current.find((b) => b.id === bp.id)) {
		moduleBps.value[groupKey] = [...current, bp]
	}
	moduleBpInputs.value[groupKey] = null
}

function removeBp(groupKey: string, idx: number) {
	moduleBps.value[groupKey] = (moduleBps.value[groupKey] ?? []).filter((_, i) => i !== idx)
}

function setModuleAsset(groupKey: string, val: SuggestedItem | undefined) {
	if (val) moduleAssets.value[groupKey] = val
	else delete moduleAssets.value[groupKey]
}

function applyPreviewResult(result: PreviewResult) {
	preview.value = result
	selection.matched = new Set((result.matched ?? []).filter(hasDiff).map((m) => m.metaId))
	selection.unmatched = new Set((result.unmatched ?? []).map((m) => m.methodId))
	for (const m of result.unmatched ?? []) {
		const key = unmatchedGroupKey(m)
		if (m.restoreConnection && m.restoreAssetId && !moduleAssets.value[key]) {
			moduleAssets.value[key] = { id: m.restoreAssetId, name: m.restoreAssetName }
		}
	}
	step.value = 2
}

async function runPreview() {
	if (!selectedFile.value) return
	isLoading.value = true
	const form = new FormData()
	form.append('file', selectedFile.value)
	const { data } = await http
		.post<Response<PreviewResult>>('/api/method/import/preview', form, { headers: { 'Content-Type': 'multipart/form-data' } })
		.finally(() => (isLoading.value = false))
	const result = data.data ?? { matched: [], unmatched: [], notFound: [], nameMismatches: [], columnInfo: { headers: [], detected: {} } }
	columnInfo.value = result.columnInfo ?? { headers: [], detected: {} }
	if (columnInfo.value.detected['name'] === undefined) {
		const mapping: Record<string, number | null> = {}
		for (const def of canonicalFieldDefs.value) mapping[def.key] = columnInfo.value.detected[def.key] ?? null
		userColumnMapping.value = mapping
		showMappingStep.value = true
	} else {
		applyPreviewResult(result)
	}
}

async function runPreviewWithMapping() {
	if (!selectedFile.value) return
	if (userColumnMapping.value['name'] === null || userColumnMapping.value['name'] === undefined) return
	isLoading.value = true
	const mapping: Record<string, number> = {}
	for (const [key, idx] of Object.entries(userColumnMapping.value)) {
		if (idx !== null && idx >= 0) mapping[key] = idx
	}
	const form = new FormData()
	form.append('file', selectedFile.value)
	form.append('columnMapping', JSON.stringify(mapping))
	const { data } = await http
		.post<Response<PreviewResult>>('/api/method/import/preview', form, { headers: { 'Content-Type': 'multipart/form-data' } })
		.finally(() => (isLoading.value = false))
	showMappingStep.value = false
	applyPreviewResult(
		data.data ?? { matched: [], unmatched: [], notFound: [], nameMismatches: [], columnInfo: { headers: [], detected: {} } },
	)
}

function goToConfirm() {
	expanded.confirmMatched.clear()
	expanded.confirmPlanned.clear()
	step.value = 3
}

async function runConfirm() {
	if (!preview.value) return
	isLoading.value = true
	const effectiveStatus = (s: string) => _effectiveStatus(s, statusReplacementMap.value)
	const updates = (preview.value.matched ?? [])
		.filter((m) => selection.matched.has(m.metaId))
		.map((m) => ({
			metaId: m.metaId,
			status: effectiveStatus(m.imported.status) || NotImplemented,
			responsibleId: m.imported.responsibleId ?? null,
			responsibleType: m.imported.responsibleType ?? null,
			dueDate: m.imported.dueDate ?? null,
			comment: m.imported.comment,
		}))
	const activations = []
	for (const m of preview.value.unmatched ?? []) {
		if (!selection.unmatched.has(m.methodId)) continue
		const key = unmatchedGroupKey(m)
		const asset = moduleAssets.value[key]
		if (m.moduleType === 'S' && (!asset?.id || isExcludedAssetSelected(key))) continue
		activations.push({
			methodId: m.methodId,
			moduleId: m.moduleId,
			moduleMetaId: m.moduleMetaId,
			assetId: asset?.id ?? 0,
			bpIds: (moduleBps.value[key] ?? []).map((b) => b.id),
			status: effectiveStatus(m.imported.status) || NotImplemented,
			responsibleId: m.imported.responsibleId ?? null,
			responsibleType: m.imported.responsibleType ?? null,
			dueDate: m.imported.dueDate ?? null,
			comment: m.imported.comment,
		})
	}
	await http.post('/api/method/import/confirm', { updates, activations }).finally(() => (isLoading.value = false))
	close()
	await editor.refresh()
}
</script>

<template>
	<IconButton v-if="canShow" data-test="import-methods-btn" class="bi bi-file-earmark-arrow-up" :title="$t('misc.import')" @click="open" />

	<Modal v-if="isVisible" v-model="isVisible" class="modal-xl modal-import-methods">
		<template #header>
			{{ $t('misc.import') }} – {{ $t('method.label', 2) }}
			<small v-if="step > 1" class="text-muted ms-2">({{ step }}/3)</small>
		</template>

		<template #body>
			<template v-if="step === 1 && !showMappingStep">
				<ImportFileUpload v-model="selectedFile" />
			</template>

			<template v-if="showMappingStep">
				<div class="alert alert-warning py-2 d-flex align-items-start gap-2">
					<i class="bi bi-exclamation-triangle-fill flex-shrink-0 mt-1"></i>
					<span>{{ $t('import.columnMappingRequired') }}</span>
				</div>
				<table class="table table-sm align-middle">
					<thead>
						<tr>
							<th class="field-col">{{ $t('import.field') }}</th>
							<th>{{ $t('import.columnInFile') }}</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="def in canonicalFieldDefs" :key="def.key">
							<td class="text-nowrap">
								{{ def.label }}
								<span v-if="def.required" class="text-danger ms-1">*</span>
							</td>
							<td>
								<select
									:value="userColumnMapping[def.key]"
									class="form-select form-select-sm status-select"
									@change="(e) => { userColumnMapping[def.key] = (e.target as HTMLSelectElement).value === '' ? null : Number((e.target as HTMLSelectElement).value) }"
								>
									<option value="">—</option>
									<option v-for="(h, i) in columnInfo.headers" :key="i" :value="i">{{ i + 1 }}. {{ h }}</option>
								</select>
							</td>
						</tr>
					</tbody>
				</table>
			</template>

			<template v-else-if="step === 2 && preview">
				<template v-if="preview.matched.length">
					<h6 class="mb-2">{{ $t('method.label', 2) }} — {{ preview.matched.length }}</h6>
					<ImportMatchedCard
						v-for="grp in matchedGroupKeys"
						:key="grp.key"
						:grp="grp"
						:methods="matchedByGroup[grp.key]"
						:importable-methods="matchedByGroupImportable[grp.key] ?? []"
						:selected-ids="selection.matched"
						:is-expanded="expanded.matched.has(grp.key)"
						:mismatch-by-name="mismatchByName"
						:status-replacement-map="statusReplacementMap"
						:approved-version-mismatch="approvedVersionMismatch"
						@toggle="toggle(expanded.matched, grp.key)"
					/>
				</template>

				<template v-if="preview.unmatched.length">
					<h6 class="mb-2">
						{{ $t('misc.import') }} – {{ $t('misc.none') }}
						<span class="badge bg-secondary ms-1">{{ preview.unmatched.length }}</span>
					</h6>
					<ImportUnmatchedCard
						v-for="mod in unmatchedModules"
						:key="mod.groupKey"
						:mod="mod"
						:methods="unmatchedByGroup[mod.groupKey]"
						:importable-methods="unmatchedByGroupImportable[mod.groupKey] ?? []"
						:selected-ids="selection.unmatched"
						:is-expanded="expanded.unmatched.has(mod.groupKey)"
						:asset="moduleAssets[mod.groupKey]"
						:bps="moduleBps[mod.groupKey] ?? []"
						:bp-input="moduleBpInputs[mod.groupKey] ?? null"
						:excluded-asset-ids="moduleExcludedAssetIds[mod.groupKey] ?? new Set()"
						:sibling-asset-ids="moduleSiblingSelectedAssetIds[mod.groupKey] ?? new Set()"
						:restore-info="moduleRestoreInfo[mod.groupKey]"
						:is-sibling-duplicate="isSiblingDuplicateAsset(mod.groupKey)"
						:is-excluded-selected="isExcludedAssetSelected(mod.groupKey)"
						:mismatch-by-name="mismatchByName"
						:status-replacement-map="statusReplacementMap"
						:approved-version-mismatch="approvedVersionMismatch"
						@toggle="toggle(expanded.unmatched, mod.groupKey)"
						@update:asset="(val) => setModuleAsset(mod.groupKey, val)"
						@add-bp="(val) => addBp(mod.groupKey, val)"
						@remove-bp="(idx) => removeBp(mod.groupKey, idx)"
					/>
				</template>

				<ImportSummaryPanel
					:total-found-count="totalFoundCount"
					:matched-no-changes-count="matchedNoChangesCount"
					:matched-changes-count="matchedChangesCount"
					:unmatched-count="preview.unmatched.length"
					:not-found="preview.notFound"
					:name-mismatches="preview.nameMismatches"
					:no-asset-count="noAssetCount"
					:no-bp-count="noBpCount"
					:responsible-not-found-count="responsibleNotFoundCount"
					:missing-responsible-labels="missingResponsibleLabels"
					:high-sec-need-count="highSecNeedCount"
					:high-sec-deselected="highSecDeselected"
					:has-any-version-mismatch="hasAnyVersionMismatch"
					:approved-version-mismatch="approvedVersionMismatch"
					:status-mismatch-count="statusMismatchCount"
					:status-replacement-map="statusReplacementMap"
					:unique-invalid-statuses="uniqueInvalidStatuses"
					:invalid-status-counts="invalidStatusCounts"
					:selected-count="selectedCount"
					:missing-requirements-count="missingRequirementsCount"
					:ready-to-import-count="readyToImportCount"
					@toggle-high-sec-deselect="toggleHighSecDeselect"
					@update:approved-version-mismatch="(val) => (approvedVersionMismatch = val)"
					@set-status-replacement="setStatusReplacement"
				/>

				<div v-if="!hasAnythingToImport" class="alert alert-info mt-3">
					{{ $t('misc.nothing') }}
				</div>
			</template>

			<template v-else-if="step === 3 && preview">
				<ImportConfirmPanel
					:confirmed-matched-groups="confirmedMatchedGroups"
					:confirmed-matched-by-group="confirmedMatchedByGroup"
					:planned-activations-count="plannedActivations.length"
					:planned-by-group="plannedByGroup"
					:unique-planned-modules="uniquePlannedModules"
					:skipped-unmatched="skippedUnmatched"
					:matched-size="selection.matched.size"
					:expanded-confirm-matched="expanded.confirmMatched"
					:expanded-confirm-planned="expanded.confirmPlanned"
					:expanded-module-ids="expanded.moduleIds"
					:mismatch-by-name="mismatchByName"
					:status-replacement-map="statusReplacementMap"
					:module-assets="moduleAssets"
					:module-bps="moduleBps"
				/>
			</template>
		</template>

		<template #footer>
			<div class="w-100 d-inline-flex justify-content-between">
				<button type="button" class="btn btn-light" data-bs-dismiss="modal">
					{{ $t('misc.cancel') }}
				</button>

				<div class="d-flex gap-2">
					<button v-if="showMappingStep" type="button" class="btn btn-light" @click="showMappingStep = false">
						<i class="bi bi-chevron-left me-1"></i>{{ $t('misc.reset') }}
					</button>
					<button v-else-if="step > 1" type="button" class="btn btn-light" @click="step = (step - 1) as 1 | 2 | 3">
						<i class="bi bi-chevron-left me-1"></i>{{ $t('misc.reset') }}
					</button>

					<button
						v-if="showMappingStep"
						type="button"
						class="btn btn-primary"
						data-test="import-continue-btn"
						:disabled="userColumnMapping['name'] === null || userColumnMapping['name'] === undefined || isLoading"
						@click="runPreviewWithMapping"
					>
						<span v-if="isLoading" class="spinner-border spinner-border-sm me-1"></span>
						{{ $t('misc.continue') }}
					</button>

					<button
						v-else-if="step === 1"
						type="button"
						class="btn btn-primary"
						data-test="import-continue-btn"
						:disabled="!selectedFile || isLoading"
						@click="runPreview"
					>
						<span v-if="isLoading" class="spinner-border spinner-border-sm me-1"></span>
						{{ $t('misc.continue') }}
					</button>

					<span v-else-if="step === 2 && hasUnresolvedStatusMismatch" class="text-danger small align-self-center">
						<i class="bi bi-exclamation-triangle me-1"></i>{{ $t('import.resolveStatusMismatches') }}
					</span>
					<button
						v-else-if="step === 2"
						type="button"
						class="btn btn-primary"
						data-test="import-continue-btn"
						:disabled="!hasAnythingToImport"
						@click="goToConfirm"
					>
						{{ $t('misc.continue') }}
					</button>

					<button
						v-else-if="step === 3"
						type="button"
						class="btn btn-primary"
						data-test="import-confirm-btn"
						:disabled="(!selection.matched.size && !plannedActivations.length) || isLoading"
						@click="runConfirm"
					>
						<span v-if="isLoading" class="spinner-border spinner-border-sm me-1"></span>
						{{ $t('misc.import') }}
					</button>
				</div>
			</div>
		</template>
	</Modal>
</template>

<style lang="sass" scoped>
.field-col
	width: 35%

.status-select
	width: auto
</style>
